import { useCallback, useEffect } from 'react';
import { type AutomaticPresentationEligibility } from '../automaticPresentation';
import {
    DASHBOARD_NEWS_POPUP_HIDDEN_KEY,
    DASHBOARD_NEWS_SEEN_UNREAD_COUNT_KEY,
    usePersistentStorage,
} from '../storage';

type NewsTooltipEligibilityInput = Readonly<{
    unreadCount: number | undefined;
    seenUnreadCount: number;
    canShowTooltip: boolean;
    isPopupHidden: boolean;
}>;

/**
 * Определяет участие News в общей очереди: скрытая подсказка исключается, загрузка счётчика ожидается,
 * а показ разрешается только при появлении новых непрочитанных новостей.
 *
 * @kind helper
 * @returns Текущий статус допуска News-подсказки к общей очереди.
 */
function getNewsTooltipEligibility({
    unreadCount,
    seenUnreadCount,
    canShowTooltip,
    isPopupHidden,
}: NewsTooltipEligibilityInput): AutomaticPresentationEligibility {
    if (!canShowTooltip || isPopupHidden) {
        return 'ineligible';
    }

    if (unreadCount === undefined) {
        return 'pending';
    }

    return unreadCount > seenUnreadCount ? 'eligible' : 'ineligible';
}

export const useNewsSeenUnreadCount = (unreadCount: number | undefined, canShowTooltip: boolean) => {
    const [seenUnreadCountRaw, setSeenUnreadCount] = usePersistentStorage(DASHBOARD_NEWS_SEEN_UNREAD_COUNT_KEY);
    const [popupHiddenRaw] = usePersistentStorage(DASHBOARD_NEWS_POPUP_HIDDEN_KEY);

    const seenUnreadCount = seenUnreadCountRaw === null ? 0 : Number(seenUnreadCountRaw);
    const isPopupHidden = popupHiddenRaw === '1';
    const resolvedUnreadCount = unreadCount ?? 0;

    const markCurrentAsSeen = useCallback(
        () => setSeenUnreadCount(String(resolvedUnreadCount)),
        [resolvedUnreadCount, setSeenUnreadCount]
    );

    useEffect(() => {
        if (unreadCount !== undefined && unreadCount < seenUnreadCount) {
            markCurrentAsSeen();
        }
    }, [unreadCount, seenUnreadCount, markCurrentAsSeen]);

    const hasUnreadMessages = resolvedUnreadCount > 0;
    const tooltipEligibility = getNewsTooltipEligibility({
        unreadCount,
        seenUnreadCount,
        canShowTooltip,
        isPopupHidden,
    });

    return { tooltipEligibility, hasUnreadMessages, markCurrentAsSeen };
};
